import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import TabletLayoutPage, { TabletContainer } from "../../components/Layout/TabletLayout";
import { MainColumn, RightRail, TwoColumnGrid } from "../../components/Layout/TwoColumn";
import { usePlayerPageController } from "../../../hooks/usePlayerPageController";
import {
  PlayerHeader,
  PlayerStatsPanel,
  PlayerSeasonStats,
  PlayerGeneralInfo,
  PlayerLastMatch,
  PlayerCareerPanel,
  CareerLegend
} from "../../components/Player";
import { AdPositionEnum } from "../../../client/api";
import { AdSlot } from "../../components/AdSlot";
import Loading from "../../components/Loading";

const PlayerPage: React.FC = () => {
  const { playerId: playerIdParam } = useParams<{ playerId?: string }>();

  const playerId = useMemo(() => {
    const n = playerIdParam ? parseInt(playerIdParam, 10) : NaN;
    return Number.isFinite(n) ? n : undefined;
  }, [playerIdParam]);

  const {
    l,
    player,
    loading,
    playerName,
    mainTeam,
    mainTeamPosition,
    mainLeague,
    shouldShowRating,
    stats,
    seasonStats,
    lastMatch,
    age,
    footLabel,
    formatCareerPeriod,
    seniorCareer,
    youthCareer
  } = usePlayerPageController(playerId);

  if (loading && !player) {
    return (
      <TabletLayoutPage>
        <TabletContainer className="PlayerPage">
          <div className="flex items-center justify-center min-h-[200px]">
            <Loading />
          </div>
        </TabletContainer>
      </TabletLayoutPage>
    );
  }

  return (
    <TabletLayoutPage>
      <TabletContainer className="PlayerPage">
        <TwoColumnGrid>
          <MainColumn>
            <div className="flex flex-col relative">
              <PlayerHeader
                player={player}
                playerName={playerName}
                mainTeam={mainTeam}
                loading={loading}
                l={l}
              />

              {player && (
                <div className="mb-4 block xl:hidden">
                  <PlayerGeneralInfo
                    height={player.height}
                    age={age}
                    footLabel={footLabel}
                    mainTeamPosition={mainTeamPosition}
                    mainLeague={mainLeague}
                    l={l}
                  />
                </div>
              )}

              {player && (stats || shouldShowRating) && (
                <PlayerStatsPanel
                  stats={stats}
                  rating={player.rating}
                  shouldShowRating={shouldShowRating}
                  l={l}
                />
              )}

              {player && seasonStats && seasonStats.length > 0 && (
                <PlayerSeasonStats seasonStats={seasonStats} />
              )}

              {player && lastMatch && (
                <PlayerLastMatch lastMatch={lastMatch} />
              )}

              {player && (
                <div className="mb-4 xl:hidden">
                  <PlayerCareerPanel
                    titleKey="seniorCareer"
                    career={seniorCareer}
                    formatPeriod={formatCareerPeriod}
                    l={l}
                  />
                </div>
              )}

              {player && (
                <div className="mb-4 xl:hidden">
                  <PlayerCareerPanel
                    titleKey="youthCareer"
                    career={youthCareer}
                    formatPeriod={formatCareerPeriod}
                    l={l}
                  />
                </div>
              )}

              <div className="xl:hidden">
                <CareerLegend l={l} />
              </div>
            </div>
          </MainColumn>

          <RightRail>
            {player && (
              <PlayerGeneralInfo
                height={player.height}
                age={age}
                footLabel={footLabel}
                mainTeamPosition={mainTeamPosition}
                mainLeague={mainLeague}
                l={l}
              />
            )}

            {player && (
              <PlayerCareerPanel
                titleKey="seniorCareer"
                career={seniorCareer}
                formatPeriod={formatCareerPeriod}
                l={l}
              />
            )}

            {player && (
              <AdSlot
                type={AdPositionEnum.PLAYER_RIGHT_RAIL}
                className="w-full"
                priority="adsense"
              />
            )}

            {player && (
              <PlayerCareerPanel
                titleKey="youthCareer"
                career={youthCareer}
                formatPeriod={formatCareerPeriod}
                l={l}
              />
            )}

            <CareerLegend l={l} />
          </RightRail>
        </TwoColumnGrid>
      </TabletContainer>
    </TabletLayoutPage>
  );
};

export default PlayerPage;
